/**
 * Mock feed — my-wardrobe (ONE kind `item`, `payload.type` discriminates).
 *
 * The wardrobe's whole point is that four different `type` values fold into
 * one identity space, so the mock carries all of them:
 *   piece  ×6 across the 5 category buckets (two share a capture session, so
 *          the Photos tab has a multi-piece grid)
 *   outfit ×2 — one with a generated on-model image, one WITHOUT (the tile
 *          must compose a flat-lay from its garments' cutouts)
 *   model  ×1 — pins a photo, which badges its tile in Photos
 *   edit   ×1 — renames a piece IN PLACE (never a second tile)
 */
const DAY = 86400000;
const at = (o, h = 10) => {
  const d = new Date(Date.now() + o * DAY);
  d.setHours(h, 0, 0, 0);
  return d.toISOString();
};

const IMG = {
  tee: 'https://images.unsplash.com/photo-1521572163474-6864f9cf17ab?w=400&q=80',
  knit: 'https://images.unsplash.com/photo-1576871337622-98d48d1cf531?w=400&q=80',
  jacket: 'https://images.unsplash.com/photo-1551028719-00167b16eac5?w=400&q=80',
  jeans: 'https://images.unsplash.com/photo-1542272604-787c3835535d?w=400&q=80',
  skirt: 'https://images.unsplash.com/photo-1583496661160-fb5886a0aaaa?w=400&q=80',
  sneakers: 'https://images.unsplash.com/photo-1595950653106-6c9ebd614d3a?w=400&q=80',
  bag: 'https://images.unsplash.com/photo-1584917865442-de89df76afd3?w=400&q=80',
  look: 'https://images.unsplash.com/photo-1483985988355-763728e1935b?w=400&q=80',
  model: 'https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?w=400&q=80',
  shot1: 'https://images.unsplash.com/photo-1490481651871-ab68de25d43d?w=600&q=80',
  shot2: 'https://images.unsplash.com/photo-1445205170230-053b83016050?w=600&q=80',
};

const pieces = [
  { item_id: 'p1', type: 'piece', name: 'White cotton tee', category: 'tops', primary_color: 'white', image_url: IMG.tee, tags: ['basic', 'summer'], session: 's1', photo: IMG.shot1 },
  { item_id: 'p2', type: 'piece', name: 'Cream cable knit', category: 'knit', primary_color: 'cream', image_url: IMG.knit, tags: ['winter'], session: 's1', photo: IMG.shot1 },
  { item_id: 'p3', type: 'piece', name: 'Denim jacket', category: 'jacket', primary_color: 'indigo', image_url: IMG.jacket, tags: ['layer'], session: 's2', photo: IMG.shot2 },
  { item_id: 'p4', type: 'piece', name: 'Straight jeans', category: 'jeans', primary_color: 'blue', image_url: IMG.jeans, tags: ['denim'], session: 's2', photo: IMG.shot2 },
  { item_id: 'p5', type: 'piece', name: 'Pleated midi skirt', category: 'skirt', primary_color: 'olive', image_url: IMG.skirt, session: 's3', photo: IMG.shot1 },
  { item_id: 'p6', type: 'piece', name: 'Canvas sneakers', category: 'sneakers', primary_color: 'off-white', image_url: IMG.sneakers, session: 's3', photo: IMG.shot1 },
  { item_id: 'p7', type: 'piece', name: 'Leather tote', category: 'bag', primary_color: 'tan', image_url: IMG.bag, session: 's3', photo: IMG.shot1 },
];

const entries = [
  ...pieces.map((p, i) => ({
    id: `e-${p.item_id}`,
    kind: 'item',
    payload: {
      item_id: p.item_id, type: p.type, name: p.name, category: p.category,
      primary_color: p.primary_color, tags: p.tags, image_url: p.image_url, photo_url: p.photo,
    },
    source_session_id: p.session,
    log_card_id: `card-${p.item_id}`,
    created_at: at(-(pieces.length - i), 9),
  })),
  // an `edit` row — folds INTO p5, never a second tile
  {
    id: 'e-p5-edit',
    kind: 'item',
    payload: { item_id: 'p5', type: 'edit', name: 'Olive pleated midi', tags: ['work', 'autumn'] },
    source_session_id: 's3',
    created_at: at(-1, 11),
  },
  {
    id: 'e-o1',
    kind: 'item',
    payload: {
      item_id: 'o1', type: 'outfit', name: 'Weekend layers', occasion: 'casual',
      description: 'Denim over the cable knit with straight jeans.',
      garment_ids: ['p2', 'p3', 'p4', 'p6'], image_url: IMG.look, model_photo_url: IMG.model,
    },
    source_session_id: 's4',
    created_at: at(-1, 15),
  },
  {
    // NO image_url — the tile must compose a flat-lay from the cutouts.
    id: 'e-o2',
    kind: 'item',
    payload: {
      item_id: 'o2', type: 'outfit', name: 'Office midi', occasion: 'work',
      description: 'Tee tucked into the pleated midi with the tote.',
      garment_ids: ['p1', 'p5', 'p7'],
    },
    source_session_id: 's5',
    created_at: at(0, 12),
  },
  {
    id: 'e-model',
    kind: 'item',
    payload: { type: 'model', photo_url: IMG.shot1 },
    source_session_id: 's1',
    created_at: at(0, 13),
  },
];

export const myWardrobeMock = {
  widget_id: 'my-wardrobe',
  instance_id: 'mock-my-wardrobe',
  settings: {},
  // Base mock = ACTIVE only. The studio injects pending/deleting on demand from
  // the State chips (studio/mockStates.js), so the default render is clean.
  pending: { adds: [], updates: [], deleting: [], deleting_grids: [] },
  entries,
};

export default myWardrobeMock;
